

class Navbar extends HTMLElement {
  constructor () {
    super()
    this.initialized = false
  }

  connectedCallback () {
    if (this.initialized) return
    this.initialized = true

    this.handleDropdownsBackdrop()
    this.enableHoverDropdowns()
    this.handleHeaderReveal()
    this.adjustOffcanvasMenu()
    this.handleOffcanvasOnResize()
  }

  adjustOffcanvasMenu () {
    const offcanvasMenu = document.querySelector('#offcanvas-menu')
    const navbarMobile = document.querySelector('#navbar-mobile')
    const headerGroup = document.querySelector('#header-group')
    const documentElement = document.documentElement
    const mobileMq = window.matchMedia('(max-width: 1199.98px)')
    const offcanvasBody = offcanvasMenu?.querySelector('.offcanvas-body')
    const offcanvasMenuToggles = document.querySelectorAll('[href="#offcanvas-menu"][data-bs-toggle="offcanvas"], [data-bs-target="#offcanvas-menu"][data-bs-toggle="offcanvas"]')
    let syncRafId = null
    let bodyScrollLockY = 0
    let lockedTouchStartY = 0

    if (offcanvasMenu && offcanvasMenu.parentElement !== document.body) {
      document.body.appendChild(offcanvasMenu)
    }

    const setOffcanvasMenuReadyState = (isReady) => {
      documentElement.classList.toggle('offcanvas-menu-ready', isReady)
      offcanvasMenuToggles.forEach((toggleEl) => {
        toggleEl.classList.toggle('offcanvas-menu-toggle-pending', !isReady)
        if (isReady) {
          toggleEl.removeAttribute('aria-disabled')
        } else {
          toggleEl.setAttribute('aria-disabled', 'true')
        }
      })
    }

    setOffcanvasMenuReadyState(false)

    const resetOffcanvasMenuCollapses = () => {
      if (!offcanvasMenu) return

      offcanvasMenu.querySelectorAll('.collapsing, .collapse').forEach((collapseEl) => {
        const instance = window.bootstrap?.Collapse?.getInstance?.(collapseEl)
        if (instance?.dispose) instance.dispose()

        collapseEl.classList.remove('show', 'collapsing')
        collapseEl.classList.add('collapse')
        collapseEl.style.height = ''
        collapseEl.style.display = ''
      })

      offcanvasMenu
        .querySelectorAll('[data-bs-toggle="collapse"][aria-expanded="true"]')
        .forEach((triggerEl) => triggerEl.setAttribute('aria-expanded', 'false'))
    }

    const clearOffcanvasMenuLayout = () => {
      if (!offcanvasMenu) return

      documentElement.style.removeProperty('--offcanvas-menu-top')
      offcanvasMenu.style.removeProperty('--offcanvas-menu-top')
      offcanvasMenu.style.top = ''
      offcanvasMenu.style.bottom = ''
      offcanvasMenu.style.height = ''
      offcanvasMenu.style.maxHeight = ''
    }

    const getPinnedElementBottom = (element) => {
      if (!(element instanceof HTMLElement)) return 0

      const rectBottom = Math.round(element.getBoundingClientRect().bottom)
      const computedStyle = window.getComputedStyle(element)
      const isPinned = computedStyle.position === 'sticky' || computedStyle.position === 'fixed'

      if (!isPinned || !document.body.classList.contains('offcanvas-menu-scroll-locked')) {
        return rectBottom
      }

      // Once the page is scroll-locked, sticky elements can report a negative
      // rect even though they remain visually pinned at the top of the viewport.
      // Preserve the visible header height so the offcanvas still opens beneath it.
      const pinnedHeight = Math.round(element.offsetHeight || element.getBoundingClientRect().height || 0)
      return Math.max(rectBottom, pinnedHeight, 0)
    }

    const syncOffcanvasMenuLayout = () => {
      if (!offcanvasMenu || !navbarMobile || !mobileMq.matches) {
        clearOffcanvasMenuLayout()
        return
      }

      const navbarBottom = Math.round(navbarMobile.getBoundingClientRect().bottom)
      const headerGroupBottom = getPinnedElementBottom(headerGroup)
      const menuTop = Math.max(navbarBottom, headerGroupBottom, 0)

      // Keep the top offset in a CSS variable so mobile height can be computed
      // entirely in CSS, without falling back to Bootstrap's 30vh default.
      documentElement.style.setProperty('--offcanvas-menu-top', `${menuTop}px`)
      offcanvasMenu.style.setProperty('--offcanvas-menu-top', `${menuTop}px`)
      offcanvasMenu.style.top = `${menuTop}px`
      offcanvasMenu.style.bottom = '0'
      offcanvasMenu.style.height = 'auto'
      offcanvasMenu.style.maxHeight = 'none'
    }

    const scheduleOffcanvasMenuLayoutSync = () => {
      if (syncRafId) {
        window.cancelAnimationFrame(syncRafId)
      }

      syncOffcanvasMenuLayout()

      syncRafId = window.requestAnimationFrame(() => {
        syncOffcanvasMenuLayout()
        syncRafId = window.requestAnimationFrame(() => {
          syncOffcanvasMenuLayout()
          syncRafId = null
        })
      })
    }

    const lockPageScroll = () => {
      if (!mobileMq.matches || document.body.classList.contains('offcanvas-menu-scroll-locked')) return

      bodyScrollLockY = window.scrollY || window.pageYOffset || 0
      documentElement.classList.add('offcanvas-menu-scroll-locked')
      documentElement.style.overflow = 'hidden'
      documentElement.style.overscrollBehavior = 'none'
      document.body.classList.add('offcanvas-menu-scroll-locked')
      document.body.style.position = 'fixed'
      document.body.style.top = `-${bodyScrollLockY}px`
      document.body.style.left = '0'
      document.body.style.right = '0'
      document.body.style.width = '100%'
      document.body.style.overflow = 'hidden'
      document.addEventListener('touchstart', handleLockedTouchStart, { passive: true, capture: true })
      document.addEventListener('touchmove', handleLockedTouchMove, { passive: false, capture: true })
      document.addEventListener('wheel', handleLockedWheel, { passive: false, capture: true })
    }

    const unlockPageScroll = () => {
      if (!document.body.classList.contains('offcanvas-menu-scroll-locked')) return

      documentElement.classList.remove('offcanvas-menu-scroll-locked')
      documentElement.style.overflow = ''
      documentElement.style.overscrollBehavior = ''
      document.body.classList.remove('offcanvas-menu-scroll-locked')
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.left = ''
      document.body.style.right = ''
      document.body.style.width = ''
      document.body.style.overflow = ''
      document.removeEventListener('touchstart', handleLockedTouchStart, true)
      document.removeEventListener('touchmove', handleLockedTouchMove, true)
      document.removeEventListener('wheel', handleLockedWheel, true)
      window.scrollTo(0, bodyScrollLockY)
    }

    const getScrollableTouchContainer = (target) => {
      if (!offcanvasBody || !(target instanceof Element) || !offcanvasBody.contains(target)) return null

      let current = target
      while (current && current !== offcanvasBody) {
        if (current.scrollHeight > current.clientHeight + 1) {
          const overflowY = window.getComputedStyle(current).overflowY
          if (overflowY === 'auto' || overflowY === 'scroll') return current
        }
        current = current.parentElement
      }

      return offcanvasBody
    }

    const handleLockedTouchStart = (event) => {
      lockedTouchStartY = event.touches?.[0]?.clientY ?? 0
    }

    const canScrollContainer = (scrollContainer, deltaY) => {
      if (!scrollContainer) return false
      if (scrollContainer.scrollHeight <= scrollContainer.clientHeight + 1) return false

      const isPullingDown = deltaY > 0
      const isPushingUp = deltaY < 0
      const isAtTop = scrollContainer.scrollTop <= 0
      const isAtBottom = Math.ceil(scrollContainer.scrollTop + scrollContainer.clientHeight) >= scrollContainer.scrollHeight

      if ((isAtTop && isPullingDown) || (isAtBottom && isPushingUp)) return false
      return true
    }

    const handleLockedTouchMove = (event) => {
      if (!document.body.classList.contains('offcanvas-menu-scroll-locked')) return

      const scrollContainer = getScrollableTouchContainer(event.target)
      const currentY = event.touches?.[0]?.clientY ?? lockedTouchStartY
      const deltaY = currentY - lockedTouchStartY

      if (!canScrollContainer(scrollContainer, deltaY)) {
        event.preventDefault()
      }

      lockedTouchStartY = currentY
    }

    const handleLockedWheel = (event) => {
      if (!document.body.classList.contains('offcanvas-menu-scroll-locked')) return

      const scrollContainer = getScrollableTouchContainer(event.target)
      if (!canScrollContainer(scrollContainer, -event.deltaY)) {
        event.preventDefault()
      }
    }

    // When navigating away from the menu (e.g. clicking a collection link) and coming back
    // via the browser back button, some browsers restore the previous DOM via bfcache.
    // In that case, the submenu state may be restored as "open", leaving a big gap under parent items.
    window.addEventListener('pageshow', (event) => {
      unlockPageScroll()
      resetOffcanvasMenuCollapses()
      clearOffcanvasMenuLayout()

      if (!event?.persisted) return

      if (offcanvasMenu?.classList.contains('show') && window.bootstrap?.Offcanvas) {
        const instance = window.bootstrap.Offcanvas.getInstance(offcanvasMenu) || new window.bootstrap.Offcanvas(offcanvasMenu)
        instance.hide()
      }
    })

    offcanvasMenu?.addEventListener('show.bs.offcanvas', () => {
      lockPageScroll()
      resetOffcanvasMenuCollapses()
      headerGroup?.classList.remove('hide')
      scheduleOffcanvasMenuLayoutSync()

      navbarMobile.querySelector('.svg-icon-menu')?.classList.add('svg-icon-menu-close')
    })

    offcanvasMenu?.addEventListener('shown.bs.offcanvas', () => {
      scheduleOffcanvasMenuLayoutSync()
    })

    offcanvasMenu?.addEventListener('hide.bs.offcanvas', () => {
      navbarMobile.querySelector('.svg-icon-menu')?.classList.remove('svg-icon-menu-close')
    })

    offcanvasMenu?.addEventListener('hidden.bs.offcanvas', () => {
      unlockPageScroll()
      resetOffcanvasMenuCollapses()
      scheduleOffcanvasMenuLayoutSync()
    })

    // When the offcanvas menu is open, Bootstrap traps focus inside it.
    // Ensure tapping/focusing the navbar search input closes the menu first
    // so the user can type their query on small screens.
    const mobileSearchInput = navbarMobile?.querySelector('form input[name="q"]')
    let focusAfterHide = false
    if (mobileSearchInput && offcanvasMenu) {
      const hideMenuForSearch = () => {
        const instance = window.bootstrap.Offcanvas.getInstance(offcanvasMenu) || new window.bootstrap.Offcanvas(offcanvasMenu)
        if (offcanvasMenu.classList.contains('show')) {
          focusAfterHide = true
          instance.hide()
        }
      }
      // Use early events to close before focus is trapped
      mobileSearchInput.addEventListener('pointerdown', hideMenuForSearch)
      mobileSearchInput.addEventListener('touchstart', hideMenuForSearch, { passive: true })
      mobileSearchInput.addEventListener('focus', hideMenuForSearch)

      offcanvasMenu.addEventListener('hidden.bs.offcanvas', () => {
        if (focusAfterHide) {
          mobileSearchInput.focus()
          focusAfterHide = false
        }
      })
    }

    const syncLayoutIfOpen = () => {
      if (!offcanvasMenu?.classList.contains('show')) return
      scheduleOffcanvasMenuLayoutSync()
    }

    const prepareOffcanvasMenuOpen = () => {
      if (offcanvasMenu?.classList.contains('show')) return
      lockPageScroll()
      headerGroup?.classList.remove('hide')
      scheduleOffcanvasMenuLayoutSync()
    }

    offcanvasMenuToggles.forEach((toggleEl) => {
      toggleEl.addEventListener('pointerdown', (event) => {
        if (!documentElement.classList.contains('offcanvas-menu-ready')) {
          event.preventDefault()
          return
        }

        prepareOffcanvasMenuOpen()
      })
      toggleEl.addEventListener('click', (event) => {
        if (!documentElement.classList.contains('offcanvas-menu-ready')) {
          event.preventDefault()
          return
        }

        prepareOffcanvasMenuOpen()
      })
    })

    window.addEventListener('resize', scheduleOffcanvasMenuLayoutSync)
    window.addEventListener('orientationchange', scheduleOffcanvasMenuLayoutSync)
    window.addEventListener('load', scheduleOffcanvasMenuLayoutSync)
    window.visualViewport?.addEventListener('resize', syncLayoutIfOpen)
    window.visualViewport?.addEventListener('scroll', syncLayoutIfOpen)
    if (window.ResizeObserver && navbarMobile) {
      const navbarResizeObserver = new window.ResizeObserver(() => scheduleOffcanvasMenuLayoutSync())
      navbarResizeObserver.observe(navbarMobile)
      if (headerGroup) navbarResizeObserver.observe(headerGroup)
    }
    document.fonts?.ready?.then?.(() => scheduleOffcanvasMenuLayoutSync())
    scheduleOffcanvasMenuLayoutSync()
    window.requestAnimationFrame(() => {
      scheduleOffcanvasMenuLayoutSync()
      setOffcanvasMenuReadyState(true)
    })
  }

  enableHoverDropdowns () {
    const mq = window.matchMedia('(min-width: 1200px)')
    const container = this
    if (!container) return

    container.querySelectorAll('#navbar-desktop .dropdown').forEach((wrapper) => {
      const toggle = wrapper.querySelector('.dropdown-toggle')
      if (!toggle || !window.bootstrap || !window.bootstrap.Dropdown) return

      const instance = window.bootstrap.Dropdown.getInstance(toggle) || new window.bootstrap.Dropdown(toggle, { autoClose: 'outside' })

      let hideTimer

      const onEnter = () => {
        if (!mq.matches) return
        clearTimeout(hideTimer)
        instance.show()
        // Ensure background blur appears even if events are missed
        document.body.classList.add('navbar-dropdown-open')
      }
      const onLeave = () => {
        if (!mq.matches) return
        hideTimer = setTimeout(() => {
          instance.hide()
          // Remove blur if no dropdown remains open
          if (!document.querySelector('#navbar-desktop .dropdown-toggle.show')) {
            document.body.classList.remove('navbar-dropdown-open')
          }
        }, 150)
      }

      wrapper.addEventListener('mouseenter', onEnter)
      wrapper.addEventListener('mouseleave', onLeave)

      // Prevent click toggle on desktop, keep it for touch devices
      toggle.addEventListener('click', (e) => {
        if (!mq.matches) return
        e.preventDefault()
        e.stopPropagation()
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation()
        return false
      })

      // Allow keyboard access (Enter/Space) to toggle on desktop
      toggle.addEventListener('keydown', (e) => {
        if (!mq.matches) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          const isShown = wrapper.querySelector('.dropdown-menu')?.classList.contains('show')
          if (isShown) {
            instance.hide()
          } else {
            instance.show()
          }
        }
      })
    })

    // As a safety net, stop Bootstrap's delegated click handler in capture phase
    const stopClickOnDesktopToggles = (e) => {
      if (!mq.matches) return
      const target = e.target
      if (target && typeof target.closest === 'function' && target.closest('#navbar-desktop .dropdown .dropdown-toggle')) {
        e.preventDefault()
        e.stopPropagation()
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation()
      }
    }
    document.addEventListener('click', stopClickOnDesktopToggles, true)
  }

  handleHeaderReveal () {
    let oldScroll = window.scrollY
    const headerGroup = document.querySelector('#header-group')
    if (!headerGroup) return

    window.addEventListener('scroll', () => {
      if (document.body.classList.contains('offcanvas-menu-scroll-locked')) {
        headerGroup.classList.remove('hide')
        oldScroll = Math.max(window.scrollY, 0)
        return
      }

      const newScroll = window.scrollY
      if (newScroll > oldScroll) {
        if (newScroll > headerGroup.clientHeight) {
          headerGroup.classList.add('hide')
        }
      } else if (newScroll < oldScroll) {
        headerGroup.classList.remove('hide')
      }

      oldScroll = Math.max(window.scrollY, 0)
    })
  }

  handleDropdownsBackdrop () {
    this.querySelectorAll('#navbar-desktop .dropdown-toggle').forEach(dropdown => {
      dropdown.addEventListener('hidden.bs.dropdown', () => {
        if (!document.querySelector('#navbar-desktop .dropdown-toggle.show')) {
          document.body.classList.remove('navbar-dropdown-open')
        }
      })
      dropdown.addEventListener('show.bs.dropdown', () => {
        document.body.classList.add('navbar-dropdown-open')
      })
    })
  }

  handleOffcanvasOnResize () {
    const offcanvasMenu = document.querySelector('#offcanvas-menu')
    if (!offcanvasMenu) return

    const documentElement = document.documentElement
    const mq = window.matchMedia('(min-width: 1200px)')

    const closeIfDesktop = () => {
      if (!mq.matches) return

      // Ensure an instance exists then hide if currently shown
      const instance = window.bootstrap.Offcanvas.getInstance(offcanvasMenu) || new window.bootstrap.Offcanvas(offcanvasMenu)
      if (offcanvasMenu.classList.contains('show')) {
        instance.hide()
      }
      // Clear any inline mobile layout set on open
      documentElement.style.removeProperty('--offcanvas-menu-top')
      offcanvasMenu.style.removeProperty('--offcanvas-menu-top')
      offcanvasMenu.style.top = ''
      offcanvasMenu.style.bottom = ''
      offcanvasMenu.style.height = ''
      offcanvasMenu.style.maxHeight = ''
    }

    if (mq.addEventListener) {
      mq.addEventListener('change', closeIfDesktop)
    } else if (mq.addListener) {
      // Safari/older browsers
      mq.addListener(closeIfDesktop)
    }

    // Run once on init in case of refreshed/resized states
    closeIfDesktop()
  }
}
customElements.define('navbar-wrapper', Navbar)
